import i18n from '../i18n';

const FILTER_TYPE_IDS = [
  'product',
  'category',
  'product_type',
  'brand',
  'swatch',
  'customer',
  'customer_group',
  'customer_extrafield',
  'discount_coupon',
  'promotion',
  'size_chart',
  'order',
  'review',
];

const TYPE_SORT_ORDER = [
  'product',
  'category',
  'product_type',
  'brand',
  'swatch',
  'discount_coupon',
  'customer',
  'customer_group',
  'customer_extrafield',
  'promotion',
  'size_chart',
  'order',
  'review',
];

export const ITEMS_PAGE_SIZE = 10;

function emDash() {
  return i18n.t('common.emDash');
}

export function typeSortRank(resourceType) {
  const key = String(resourceType || '').toLowerCase();
  const idx = TYPE_SORT_ORDER.indexOf(key);
  return idx === -1 ? TYPE_SORT_ORDER.length : idx;
}

export function sortVaultItems(list) {
  return [...list].sort((a, b) => {
    const rankDiff = typeSortRank(a.resource_type) - typeSortRank(b.resource_type);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      sensitivity: 'base',
    });
  });
}

export function formatType(type) {
  if (!type) return emDash();
  const key = String(type).toLowerCase();
  const path = `vaultUi.types.${key}`;
  const translated = i18n.t(path);
  if (translated !== path) return translated;
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Orders: Ecwid CP shows #MQQGA, not the long numeric orderNumber.
 * Prefer stored rich name (#id · email · total); otherwise derive from bc_id.
 */
export function formatVaultItemName(item) {
  if (!item || typeof item !== 'object') {
    return emDash();
  }
  const type = String(item.resource_type || '').toLowerCase();
  const name = item.name != null ? String(item.name).trim() : '';
  const bcId = item.bc_id != null ? String(item.bc_id).trim() : '';

  if (type === 'order' || type === 'orders') {
    if (name.startsWith('#')) {
      return name;
    }
    const parts = [];
    if (bcId) {
      parts.push(bcId.charAt(0) === '#' ? bcId : `#${bcId}`);
    }
    // Skip bare numeric orderNumber leftovers; keep email/text if already stored.
    if (name && name !== bcId && !/^\d+$/.test(name)) {
      parts.push(name);
    }
    if (parts.length > 0) {
      return parts.join(' · ');
    }
    return name || emDash();
  }

  return name || bcId || emDash();
}

function typeLabel(resourceType) {
  const key = String(resourceType || '').toLowerCase();
  const path = `vaultUi.typeLabels.${key}`;
  const translated = i18n.t(path);
  if (translated !== path) return translated;
  return formatType(resourceType);
}

function dash(value) {
  if (value == null || value === '') return emDash();
  const text = String(value).trim();
  return text || emDash();
}

function stripHtml(value, max = 280) {
  if (value == null || value === '') return emDash();
  const text = String(value).replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  if (!text) return emDash();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function nestEntity(payload, key, type) {
  const root = payload || {};
  const nested = root[key];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested;
  }
  if (type === key || type === `${key}s`) {
    return root;
  }
  return {};
}

function s(key) {
  return i18n.t(`vaultUi.summary.${key}`);
}

export function summaryFromPayload(resourceType, payload) {
  const type = String(resourceType || '').toLowerCase();
  const root = payload || {};

  if (root.deleted === true) {
    return {
      [s('status')]: i18n.t('vaultUi.summary.deletedInLive'),
      [s('type')]: formatType(resourceType),
    };
  }

  if (type === 'product') {
    const product = root.product && typeof root.product === 'object' ? root.product : root;
    return {
      [s('name')]: dash(product.name || product.title),
      [s('type')]: typeLabel('product'),
      [s('sku')]: dash(product.sku),
      [s('description')]: stripHtml(product.description),
      [s('price')]: dash(product.price ?? product.compareToPrice),
    };
  }

  if (type === 'category') {
    const category = nestEntity(root, 'category', type);
    return {
      [s('name')]: dash(category.name),
      [s('type')]: typeLabel('category'),
      [s('parent')]: dash(category.parentId ?? category.parent_id),
      [s('description')]: stripHtml(category.description),
    };
  }

  if (type === 'product_type' || type === 'product_class') {
    const klass = nestEntity(root, 'product_type', type);
    return {
      [s('name')]: dash(klass.name),
      [s('type')]: typeLabel('product_type'),
      [s('attributes')]: Array.isArray(klass.attributes)
        ? String(klass.attributes.length)
        : emDash(),
    };
  }

  if (type === 'discount_coupon' || type === 'coupon') {
    const coupon = nestEntity(root, 'discount_coupon', type);
    return {
      [s('name')]: dash(coupon.name || coupon.code),
      [s('type')]: typeLabel('discount_coupon'),
      [s('code')]: dash(coupon.code),
      [s('status')]: dash(coupon.status),
      [s('discount')]: dash(coupon.discount),
    };
  }

  if (type === 'customer') {
    const customer = nestEntity(root, 'customer', type);
    const name = [customer.name, customer.firstName, customer.lastName].filter(Boolean).join(' ');
    return {
      [s('name')]: dash(name || customer.email),
      [s('type')]: typeLabel('customer'),
      [s('email')]: dash(customer.email),
      [s('phone')]: dash(customer.phone),
    };
  }

  if (type === 'customer_group') {
    const group = nestEntity(root, 'customer_group', type);
    return {
      [s('name')]: dash(group.name),
      [s('type')]: typeLabel('customer_group'),
      [s('members')]: dash(group.customerCount ?? group.memberCount),
    };
  }

  if (type === 'order') {
    const order = nestEntity(root, 'order', type);
    const publicId = order.id ?? order.vendorOrderNumber ?? order.orderNumber;
    return {
      [s('orderNumber')]: dash(
        publicId != null && String(publicId).trim() !== ''
          ? (String(publicId).trim().charAt(0) === '#'
            ? String(publicId).trim()
            : `#${String(publicId).trim()}`)
          : null
      ),
      [s('type')]: typeLabel('order'),
      [s('status')]: dash(order.paymentStatus ?? order.fulfillmentStatus ?? order.status),
      [s('customer')]: dash(order.email ?? order.customerEmail),
      [s('total')]: dash(order.total ?? order.orderTotal),
    };
  }

  if (type === 'promotion') {
    const promo = nestEntity(root, 'promotion', type);
    return {
      [s('name')]: dash(promo.name ?? promo.title),
      [s('type')]: typeLabel('promotion'),
      [s('status')]: dash(promo.status),
      [s('discount')]: dash(promo.discount ?? promo.discountType),
    };
  }

  if (type === 'review') {
    const review = nestEntity(root, 'review', type);
    return {
      [s('product')]: dash(review.productName),
      [s('type')]: typeLabel('review'),
      [s('rating')]: dash(review.rating),
      [s('message')]: stripHtml(review.reviewMessage ?? review.text),
    };
  }

  if (type === 'size_chart') {
    const chart = nestEntity(root, 'size_chart', type);
    return {
      [s('name')]: dash(chart.name ?? chart.title),
      [s('type')]: typeLabel('size_chart'),
      [s('products')]: Array.isArray(chart.productIds)
        ? String(chart.productIds.length)
        : emDash(),
    };
  }

  if (type === 'brand') {
    const brand = nestEntity(root, 'brand', type);
    return {
      [s('name')]: dash(brand.name ?? brand.title),
      [s('type')]: typeLabel('brand'),
    };
  }

  if (type === 'swatch') {
    const swatch = nestEntity(root, 'swatch', type);
    return {
      [s('name')]: dash(swatch.name ?? swatch.value ?? swatch.text),
      [s('type')]: typeLabel('swatch'),
    };
  }

  if (type === 'customer_extrafield') {
    const field = nestEntity(root, 'customer_extrafield', type);
    return {
      [s('name')]: dash(field.title ?? field.name ?? field.key),
      [s('type')]: typeLabel('customer_extrafield'),
      [s('key')]: dash(field.key),
    };
  }

  return {
    [s('name')]: dash(root.name || root.title || root.code || root.email),
    [s('type')]: formatType(resourceType),
    [s('id')]: dash(root.id),
  };
}

export function formatJobKind(kind) {
  const k = String(kind || '').toLowerCase();
  const path = `vaultUi.jobs.${k}`;
  const translated = i18n.t(path);
  if (translated !== path) return translated;
  return kind ? formatType(kind) : i18n.t('vaultUi.jobs.fallback');
}

export function formatRestoreAction(action) {
  const a = String(action || '').toLowerCase();
  if (a === 'created' || a === 'create' || a === 'post') return i18n.t('vaultUi.actions.created');
  if (a === 'updated' || a === 'update' || a === 'upsert' || a === 'put') {
    return i18n.t('vaultUi.actions.updated');
  }
  if (a === 'skipped' || a === 'skip') return i18n.t('vaultUi.actions.skipped');
  if (a === 'refused') return i18n.t('vaultUi.actions.refused');
  return action ? formatType(action) : emDash();
}

export { FILTER_TYPE_IDS };
